/**
 * Print-only labeled progress bar atom for Monthly Consultation PDF.
 */

import React from "react";
import { bodyFontFamily, border, charcoal, mutedGray, tanGold } from "../pdfTheme";

/**
 * Props for a labeled horizontal score / progress bar.
 */
export interface PdfScoreBarProps {
  /** Area or metric label shown above / beside the track. */
  label: string;
  /** Fill percentage in the range 0–100 (clamped). */
  pct: number;
  /** Fill color for the bar (theme token). */
  color: string;
  /**
   * Optional quality label (e.g. Excellent) shown beside the numeric score.
   * Used on the Overview glance row; omitted when empty.
   */
  qualityLabel?: string;
}

/**
 * Clamps a numeric percentage into the inclusive 0–100 range.
 *
 * @param value - Raw percentage input
 * @returns Safe percentage for width styling
 */
function clampPct(value: number): number {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return 0;
  }
  if (value < 0) {
    return 0;
  }
  if (value > 100) {
    return 100;
  }
  return value;
}

/**
 * Labeled horizontal progress bar with a thin track and colored fill.
 * Used on cover and scorecard chapters for life-area scores.
 */
export const PdfScoreBar: React.FC<PdfScoreBarProps> = ({
  label,
  pct,
  color,
  qualityLabel,
}) => {
  const safeLabel = typeof label === "string" ? label.trim() : "";
  const safePct = clampPct(pct);
  const displayPct = Math.round(safePct);
  const safeQuality =
    typeof qualityLabel === "string" && qualityLabel.trim().length > 0
      ? qualityLabel.trim()
      : null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          fontFamily: bodyFontFamily,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: charcoal,
            letterSpacing: "0.01em",
          }}
        >
          {safeLabel}
        </span>
        <span
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: mutedGray,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {safeQuality !== null
              ? `${String(displayPct)} / 100`
              : `${String(displayPct)}%`}
          </span>
          {safeQuality !== null ? (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.04em",
                color: tanGold,
              }}
            >
              {safeQuality}
            </span>
          ) : null}
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={displayPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={safeLabel.length > 0 ? safeLabel : "Score"}
        style={{
          width: "100%",
          height: 8,
          borderRadius: 999,
          backgroundColor: border,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${safePct}%`,
            height: "100%",
            borderRadius: 999,
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
};
